<script setup lang="ts">
import { provide, computed, ref, watch, type CSSProperties } from 'vue'
import type { FormContext, FormInstance, FormItemInstance, NamePath, Rule, ValidateErrorEntity } from './types'
import { normalizeNamePath, getPathValue, setPathValue, cloneDeepValue } from './validator'
import { useUiConfig } from '../config-provider/context'

export type { FormContext, FormInstance, Rule, ValidateErrorEntity, NamePath }
const props = withDefaults(defineProps<{
  model?: Record<string, any>
  rules?: Record<string, Rule | Rule[]>
  layout?: 'horizontal' | 'vertical' | 'inline'
  labelAlign?: 'left' | 'right'
  labelCol?: { span?: number; style?: CSSProperties | Record<string, string | number> }
  wrapperCol?: { span?: number; style?: CSSProperties | Record<string, string | number> }
  disabled?: boolean
  size?: 'small' | 'middle' | 'large'
  hideRequiredMark?: boolean
  scrollToFirstError?: boolean
  validateTrigger?: string | string[]
}>(), { layout: 'horizontal', labelAlign: 'right', disabled: undefined, hideRequiredMark: false, scrollToFirstError: false, validateTrigger: 'change' })
const emit = defineEmits<{
  submit: [event: Event]
  finish: [values: Record<string, any>]
  finishFailed: [errorInfo: ValidateErrorEntity]
}>()
const config = useUiConfig()
const formRef = ref<HTMLFormElement | null>(null)
const registeredFields = new Set<FormItemInstance>()
let validationVersion = 0
watch(() => props.model, () => { validationVersion++ }, { deep: true, flush: 'sync' })
const formContext = computed<FormContext>(() => ({
  ...props,
  disabled: props.disabled ?? config.value.componentDisabled,
  size: props.size ?? config.value.componentSize,
  registerField(field) { registeredFields.add(field); return () => { registeredFields.delete(field); validationVersion++ } }
}))
provide('rrFormContext', formContext)
function filterTargetFields(nameList?: NamePath[]): FormItemInstance[] {
  if (!nameList) return [...registeredFields]
  const paths = nameList.map(normalizeNamePath)
  return [...registeredFields].filter(field => field.name && paths.some(path => path.every((part, i) => part === field.name?.[i])))
}
async function validate(nameList?: NamePath[]): Promise<Record<string, any>> {
  const version = ++validationVersion
  const targets = filterTargetFields(nameList)
  const values: Record<string, any> = {}
  for (const field of targets) {
    if (field.name?.length) setPathValue(values, field.name, cloneDeepValue(getPathValue(props.model, field.name)))
  }
  const results = await Promise.all(targets.map(async field => ({ name: field.name ?? [], errors: await field.validate() })))
  const errorFields = results.filter(field => field.errors.length > 0)
  const outOfDate = version !== validationVersion
  if (errorFields.length || outOfDate) throw { values, errorFields, outOfDate } satisfies ValidateErrorEntity
  return values
}
function resetFields(nameList?: NamePath[]) {
  validationVersion++
  filterTargetFields(nameList).forEach(field => field.resetField())
}
function clearValidate(nameList?: NamePath[]) {
  validationVersion++
  filterTargetFields(nameList).forEach(field => field.clearValidate())
}
function setFields(fields: { name: NamePath; errors?: string[] }[]) {
  validationVersion++
  for (const field of fields) filterTargetFields([field.name]).forEach(target => target.setErrors?.(field.errors ?? []))
}
function scrollToField(name: NamePath, options?: ScrollIntoViewOptions) {
  const el = filterTargetFields([name])[0]?.getEl()
  el?.scrollIntoView(options ?? { behavior: 'smooth', block: 'nearest' })
  el?.querySelector<HTMLElement>('input,select,textarea,button,[tabindex="0"]')?.focus({ preventScroll: true })
}
async function submit() {
  try {
    const values = await validate()
    emit('finish', values)
  } catch (error) {
    if (!error || typeof error !== 'object' || !('errorFields' in error)) throw error
    const info = error as ValidateErrorEntity
    if (props.scrollToFirstError && !info.outOfDate && info.errorFields[0]) scrollToField(info.errorFields[0].name)
    emit('finishFailed', info)
  }
}
async function handleSubmit(event: Event) {
  event.preventDefault()
  emit('submit', event)
  await submit()
}
defineExpose<FormInstance>({ validate, validateFields: validate, resetFields, clearValidate, setFields, scrollToField, submit })
</script>

<template>
  <form ref="formRef" class="rr-form" :class="[`rr-form--${layout}`, `rr-form--label-${labelAlign}`, `rr-form--${formContext.size ?? 'middle'}`, { 'rr-form--disabled': formContext.disabled }]" @submit="handleSubmit">
    <slot />
  </form>
</template>

<style scoped>
.rr-form {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
  color: var(--rr-color-text);
  font-size: var(--rr-font-size-base, 13px);
  line-height: var(--rr-line-height-base, 1.5);
  display: flex;
  flex-direction: column;
  gap: var(--rr-space-4, 16px);
}

.rr-form--inline {
  flex-direction: row;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: var(--rr-space-3, 12px);
}

.rr-form--vertical {
  gap: var(--rr-space-3, 12px);
}
</style>
